import type { ResourceType } from "@/game/entities/config/resources";
import type {
	PriceLevels,
	PriceValue,
	UpgradeId,
	UpgradeLevel,
} from "@/game/entities/config/upgrades";
import { upgrades } from "@/game/entities/config/upgrades";
import type { EventUpgradeIds } from "@/game/entities/config/upgrades/";
import { eventUpgrades } from "@/game/entities/config/upgrades/event";
import GameError, { GameErrorType } from "@/libs/GameError";
import { type Timestamp } from "@/types";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import type { ComputedRef } from "vue";
import { computed, watch } from "vue";
import useResourcesStore from "./useResourcesStore";

// Include numeric upgrade id to distinguish event and core purchases in logs
function formatUpgradePriceReason(
	id: UpgradeId | EventUpgradeIds,
	heading: string | undefined,
	level: UpgradeLevel,
): string {
	const name = (heading ?? "").replace(/^upgrade\./i, "").toLowerCase();
	return `upgrade: price - ${id}:${name} (level ${level})`;
}

export type UpgradesStoreState = Record<
	UpgradeId | EventUpgradeIds,
	UpgradeLevel
>;

type UpgradesStoreGetters = Record<
	UpgradeId | EventUpgradeIds,
	ComputedRef<UpgradeLevel>
>;

type UpgradesStore = UpgradesStoreGetters & {
	$getPayload: () => UpgradesStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<UpgradesStoreState>) => void;
	ownedUpgradeCount: ComputedRef<number>;
	ownedUpgradeOnMaxLevelCount: ComputedRef<number>;
	getAllUpgrades: ComputedRef<UpgradesStoreState>;
	getCurrentLevelById: (id: UpgradeId | EventUpgradeIds) => number;
	increaseCurrentLevelById: (
		id: UpgradeId | EventUpgradeIds,
		isAlreadyPaid?: boolean,
	) => void;
	increaseCurrentLevelByIdAndPrice(
		id: UpgradeId | EventUpgradeIds,
		currencyType: ResourceType,
	): void;
	setUpgradeLevelById: (
		id: UpgradeId | EventUpgradeIds,
		level: UpgradeLevel,
	) => void;
};

type UpgradesStoreDiffAction = {
	// type: "set";
	key: keyof UpgradesStoreState;
	currentValue: UpgradesStoreState[keyof UpgradesStoreState];
	value: UpgradesStoreState[keyof UpgradesStoreState];
	// reason?: string | false;
	timestamp: Timestamp;
};

type UpgradesStorePayload = {
	state: UpgradesStoreState;
	actions: Array<UpgradesStoreDiffAction>;
};

const STORE_NAME = "upgrades";

export default defineStore(STORE_NAME, (): UpgradesStore => {
	const keys = Array.from(
		new Set([...upgrades.keys(), ...eventUpgrades.keys()]),
	);

	const state = createImmutableState<UpgradesStoreState>(
		keys.reduce(
			(
				acc: UpgradesStoreState,
				id: UpgradeId | EventUpgradeIds,
			): UpgradesStoreState => ({
				...acc,
				[id]: 0,
			}),
			{} as UpgradesStoreState,
		),
	);

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): UpgradesStoreState => {
		const data = getImmutableState(state).value;
		// @ts-expect-error - TS2352
		const keys = Object.keys(data) as Array<keyof UpgradesStoreState>;
		return keys.reduce(
			(
				acc: UpgradesStoreState,
				key: keyof UpgradesStoreState,
			): UpgradesStoreState => {
				if (data[key] === 0) return acc;
				return {
					...acc,
					[key]: data[key],
				};
			},
			{} as UpgradesStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<UpgradesStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<UpgradesStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: UpgradesStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): UpgradesStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<UpgradesStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const getters = keys.reduce(
		(
			acc: UpgradesStoreGetters,
			key: UpgradeId | EventUpgradeIds,
		): UpgradesStoreGetters => ({
			...acc,
			[key]: getImmutableStateProperty(state, key, null!),
		}),
		{} as UpgradesStoreGetters,
	);

	const ownedUpgradeCount = computed((): number =>
		Object.values(state.value).reduce(
			(acc: number, value: number): number => acc + value,
			0,
		),
	);

	const ownedUpgradeOnMaxLevelCount = computed((): number =>
		(Object.entries(state.value) as Array<[string, UpgradeLevel]>)
			.map(
				([key, value]): [UpgradeId | EventUpgradeIds, UpgradeLevel] => [
					+key,
					value,
				],
			)
			.reduce(
				(
					acc: number,
					[key, value]: [UpgradeId | EventUpgradeIds, UpgradeLevel],
				): number => {
					if (!upgrades.has(key) && !eventUpgrades.has(key))
						return acc;

					const upgrade =
						upgrades.get(key) ?? eventUpgrades.get(key)!;
					if (!upgrade.maxLevel) return acc;

					return value >= upgrade.maxLevel ? acc + 1 : acc;
				},
				0,
			),
	);

	const getAllUpgrades = computed((): UpgradesStoreState => state.value);

	const getCurrentLevelById = (id: UpgradeId | EventUpgradeIds): number => {
		const upgrade = getImmutableStateProperty(state, id, 0);
		return upgrade.value;
	};

	const increaseCurrentLevelById = (
		id: UpgradeId | EventUpgradeIds,
		isAlreadyPaid = false,
	): void => {
		if (!upgrades.has(id) && !eventUpgrades.has(id)) {
			throw new GameError(
				GameErrorType.DATA_ERROR,
				`Upgrade with ID ${id} does not exist.`,
			);
		}

		const currentLevel = getCurrentLevelById(id) as UpgradeLevel;
		const newLevel = currentLevel + 1;
		if (
			newLevel >
			(upgrades.get(id)?.maxLevel ?? eventUpgrades.get(id)!.maxLevel ?? 0)
		)
			return;

		const upgrade = upgrades.get(id) ?? eventUpgrades.get(id);

		if (!isAlreadyPaid) {
			const upgradePrices = upgrade?.price as PriceLevels;
			const upgradePricesForNewLevel =
				(upgradePrices?.[newLevel] as PriceValue) || [];

			let validResourcesAmount = true;
			const entries = Object.entries(
				upgradePricesForNewLevel || {},
			) as Array<[ResourceType, number]>;

			if (entries.length) {
				validResourcesAmount = entries.every(
					([key, value]: [ResourceType, number]): boolean =>
						useResourcesStore().validateResourceAmount(key, value),
				);
			}

			if (!validResourcesAmount) return;

			const upgradeTitle = Array.isArray(upgrade?.heading)
				? upgrade.heading[0]
				: upgrade?.heading;

			entries.forEach(([key, value]: [ResourceType, number]): void =>
				useResourcesStore().decrease(
					key,
					value,
					formatUpgradePriceReason(id, upgradeTitle, newLevel),
				),
			);
		}

		setImmutableStateProperty(state, id, newLevel);
	};

	const increaseCurrentLevelByIdAndPrice = (
		id: UpgradeId,
		currencyType: ResourceType,
	): void => {
		if (!upgrades.has(id) && !eventUpgrades.has(id)) {
			throw new GameError(
				GameErrorType.DATA_ERROR,
				`Upgrade with ID ${id} does not exist.`,
			);
		}

		const currentLevel = getCurrentLevelById(id) as UpgradeLevel;
		const newLevel = currentLevel + 1;
		if (
			newLevel >
			(upgrades.get(id)?.maxLevel ?? eventUpgrades.get(id)!.maxLevel ?? 0)
		)
			return;

		const upgrade = upgrades.get(id) ?? eventUpgrades.get(id);

		const upgradePrices = upgrade?.price as PriceLevels;
		const upgradePricesForNewLevel =
			(upgradePrices?.[newLevel] as PriceValue) || [];

		let validResourcesAmount = true;
		const entries = Object.entries(upgradePricesForNewLevel || {}) as Array<
			[ResourceType, number]
		>;
		// Filter only currency types from entries
		const currencyEntries = entries.filter(
			([key]: [ResourceType, number]): boolean => currencyType === key,
		);
		if (currencyEntries.length) {
			validResourcesAmount = currencyEntries.every(
				([key, value]: [ResourceType, number]): boolean =>
					useResourcesStore().validateResourceAmount(key, value),
			);
		}
		if (!validResourcesAmount) return;

		const upgradeTitle = Array.isArray(upgrade?.heading)
			? upgrade.heading[0]
			: upgrade?.heading;
		currencyEntries.forEach(([key, value]: [ResourceType, number]): void =>
			useResourcesStore().decrease(
				key,
				value,
				formatUpgradePriceReason(id, upgradeTitle, newLevel),
			),
		);

		setImmutableStateProperty(state, id, newLevel);
	};

	const setUpgradeLevelById = (
		id: UpgradeId | EventUpgradeIds,
		level: UpgradeLevel,
	): void => {
		if (!upgrades.has(id) && !eventUpgrades.has(id)) {
			throw new GameError(
				GameErrorType.DATA_ERROR,
				`Upgrade with ID ${id} does not exist.`,
			);
		}
		if (
			level < 0 ||
			level >
				(upgrades.get(id)?.maxLevel ??
					eventUpgrades.get(id)!.maxLevel ??
					0)
		) {
			throw new GameError(
				GameErrorType.DATA_ERROR,
				`Invalid level ${level} for upgrade with ID ${id}.`,
			);
		}
		setImmutableStateProperty(state, id, level);
	};

	// @ts-expect-error - TS2352
	(Object.keys(state.value) as Array<keyof UpgradesStoreState>).forEach(
		(key: keyof UpgradesStoreState): void => {
			watch(
				(): UpgradesStoreState[keyof UpgradesStoreState] =>
					state.value[key],
				(
					value: UpgradesStoreState[keyof UpgradesStoreState],
					currentValue: UpgradesStoreState[keyof UpgradesStoreState],
				): void => {
					$actions.push({
						key,
						currentValue,
						value,
						timestamp: Date.now(),
					});
				},
			);
		},
	);

	return {
		$getPayload,
		$resetPayload,
		$setState,
		...getters,
		ownedUpgradeCount,
		ownedUpgradeOnMaxLevelCount,
		getAllUpgrades,
		getCurrentLevelById,
		increaseCurrentLevelById,
		increaseCurrentLevelByIdAndPrice,
		setUpgradeLevelById,
	};
});
